const NOTIFICATIONS_ENABLED_KEY = "omnio-system-notifications-enabled";
const recentlyDisplayed = new Map();
const DEDUPE_WINDOW_MS = 30000;

export const supportsSystemNotifications = () =>
  typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator;

export const getSystemNotificationState = () => {
  if (!supportsSystemNotifications()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission !== "granted") return "prompt";
  return window.localStorage.getItem(NOTIFICATIONS_ENABLED_KEY) === "false"
    ? "disabled"
    : "enabled";
};

export const requestSystemNotificationPermission = async () => {
  if (!supportsSystemNotifications()) return "unsupported";

  const permission = await Notification.requestPermission();
  if (permission === "granted") {
    window.localStorage.setItem(NOTIFICATIONS_ENABLED_KEY, "true");
    return "enabled";
  }
  return permission === "denied" ? "denied" : "prompt";
};

export const setSystemNotificationsEnabled = (enabled) => {
  if (!supportsSystemNotifications() || Notification.permission !== "granted") {
    return getSystemNotificationState();
  }
  window.localStorage.setItem(NOTIFICATIONS_ENABLED_KEY, enabled ? "true" : "false");
  return enabled ? "enabled" : "disabled";
};

const notificationText = (alert) => {
  const payload = alert?.raw?.payload ?? alert?.raw?.envelope?.data ?? {};
  const title = payload?.title ?? payload?.heading ?? payload?.subject ?? "OmnioFarm alert";
  const body =
    payload?.message ??
    payload?.body ??
    payload?.description ??
    payload?.detail ??
    alert?.title ??
    "A new alert was received.";

  return {
    title: String(title || "OmnioFarm alert"),
    body: String(body || "A new alert was received."),
  };
};

export const showSystemNotification = async (alert) => {
  if (getSystemNotificationState() !== "enabled" || !alert) return false;

  try {
    const { title, body } = notificationText(alert);
    const sequenceId = alert?.raw?.envelope?.sequenceId ?? alert?.raw?.sequenceId ?? "";
    const dedupeKey = `${alert.group ?? ""}|${sequenceId}|${title}|${body}`;
    const now = Date.now();
    const lastDisplayedAt = recentlyDisplayed.get(dedupeKey);
    if (lastDisplayedAt && now - lastDisplayedAt < DEDUPE_WINDOW_MS) return false;

    recentlyDisplayed.forEach((displayedAt, key) => {
      if (now - displayedAt >= DEDUPE_WINDOW_MS) recentlyDisplayed.delete(key);
    });
    recentlyDisplayed.set(dedupeKey, now);

    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      body,
      icon: "/icons/icon-192.png",
      badge: "/icons/icon-192.png",
      tag: `omnio-alert-${alert.id}`,
      data: { url: "/notifications", alertId: alert.id },
      renotify: true,
    });
    return true;
  } catch (error) {
    console.warn("Unable to display system notification", error);
    return false;
  }
};
